import useSWR from 'swr';

import { type PublicPageEnv, UnsafeClientRuntimeEnvironmentSchema } from '@/src/environment';
import { logger } from '@/src/internal/logger';

/**
 * The SWR cache key under which the fallback client-environment request is stored.
 */
const ClientEnvironmentCacheKey = '/api/client-environment';

/**
 * How long a fetched client environment is deduplicated by SWR before a new request is allowed.
 * The environment only changes on deployment, so five minutes comfortably outlives any burst of
 * consumers while still converging shortly after a deploy.
 */
const ClientEnvironmentCacheExpirationMs = 5 * 60 * 1000;

/**
 * Fetches and validates the client environment from the fallback endpoint.
 *
 * The request deliberately uses the bare `fetch` API rather than the application's HTTP client:
 * the `EnvironmentProvider` this serves sits above the rest of the application's provider tree,
 * and the fallback is a single same-origin JSON request whose response is validated directly
 * against {@link UnsafeClientRuntimeEnvironmentSchema}, so the HTTP client stack adds nothing
 * here.
 *
 * A failed request or a non-conforming response throws, so SWR surfaces the failure through its
 * own retry policy rather than caching a permanently empty result.
 *
 * @param {string} url The fallback endpoint URL, supplied by SWR from the cache key.
 *
 * @throws {Error} If the request fails or the response does not conform to the schema.
 *
 * @returns {Promise<PublicPageEnv>} The validated client environment.
 */
const fetchClientEnvironmentFallback = async (url: string): Promise<PublicPageEnv> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `The client environment request failed with a status code of ${response.status}.`,
    );
  }
  const parsed = UnsafeClientRuntimeEnvironmentSchema.safeParse(await response.json());
  if (parsed.error) {
    throw new Error(
      'The client environment response did not conform to the expected schema: ' +
        parsed.error.message,
    );
  }
  return parsed.data;
};

export interface UseApiClientEnvironmentProps {
  /**
   * Whether or not the fetch for the API client environment should be made.
   *
   * In cases where the server-injected environment is available, this fetch is not necessary and
   * should be disabled, as the information is already available via the server-injected context
   * for server-side rendered pages.
   *
   * The only pages for which this should be enabled are public pages that are not server-side
   * rendered.
   */
  readonly isEnabled: boolean;
}

/**
 * A hook that fetches the {@link PublicPageEnv} from the API endpoint '/api/client-environment'
 * when no server-injected environment is available - which should only ever pertain to public
 * pages that are not server-side rendered.
 *
 * @param {UseApiClientEnvironmentProps} props Controls whether the fetch is performed at all.
 *
 * @returns {PublicPageEnv | undefined}
 *   The fetched environment, or {@link undefined} while the request is disabled, in flight, or
 *   failed (SWR retries failures on its own schedule).
 */
export const useApiClientEnvironment = ({ isEnabled }: UseApiClientEnvironmentProps) => {
  const { data } = useSWR(
    isEnabled ? ClientEnvironmentCacheKey : null,
    fetchClientEnvironmentFallback,
    {
      dedupingInterval: ClientEnvironmentCacheExpirationMs,
      onError: error => logger.error(error, 'There was an error loading the client environment.'),
      revalidateOnFocus: false,
    },
  );
  return data;
};
